import { usePymbleApi } from "./usePymbleApi";
import { app } from "../config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { TimetableType } from "./useTimetablePeriods";

const MY_LESSON_END_POINT = "/timetable/lesson/";
const MY_LESSON_DELETE_END_POINT = "/timetable/admin/lesson/";
const LESSON_COVER_TEACHERS_END_POINT = "/timetable/admin/lesson/cover/";
const LESSON_COVER_EVENT_END_POINT = "/timetable/admin/lesson/event/";

export type LessonDetail = {
  _id?: string | null;
  CoverWork?: {
    Summary?: string;
    Detail?: string;
    Resources?: string;
    PrivateNotes?: string;
    Files?: any[];
  };
  LessonStart?: string;
  LessonEnd?: string;
  LessonStartTime?: string;
  LessonEndTime?: string;
  Type?: string;
  Absence?: {
    _id: string | null;
    Teacher: {
      _id: string;
      ContactId: number;
      Name: string;
    };
  };
  Colour?: string;
  CoverTeachers?: any[];
  Room?: {
    Short?: string;
    ForeignKey?: string;
    Swap?: {
      SwappedAt?: string;
      SwappedBy?: string;
      ForeignKey?: string;
      Short?: string;
      Reason?: string;
    };
  };
  Title?: string;
  Class?: {
    Title?: string;
    RecordKey?: string;
    CoverTeachersRequired?: number;
    LMSUrl?: string;
    PeriodClassId?: number;
    CalendarDate?: string;
    PeriodId?: number;
    TimetableId?: number;
    PeriodCycleDayId?: number;
    ClassId?: number;
    TimetableType?: TimetableType;
    PeriodName?: string;
    PeriodNameShort?: string;
    Code?: string;
    NameShort?: string;
    NameLong?:string;
    Teacher?: {
      _id?: string;
      ContactId?: number;
      Name?: string;
      Code?: string;
    };
  };
  RequiresCover?: boolean | null;
  DeletedAt?: string;
};

export type LessonEventRequest = {
  Date: string;
  Title: string;
  Periods: number[];
  Teachers: string[];
}

/**
 * Hook for separate loading of lesson detail data
 * for components.
 * Requires lessonId to be passed in.
 */
export function useLessonDetail(lesson_id: string | undefined | null) {
  const [lessonSaving, setLessonSaving] = useState(false);
  const [isUpdateError, setIsUpdateError] = useState(false);
  const [updateError, setUpdateError] = useState<any>(null);
  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();
  const queryClient = useQueryClient();

  // Load the lesson from api
  const { isLoading, isError, data, error } = useQuery({
    queryKey: ["my-lesson", lesson_id],
    queryFn: () => callApi("GET", MY_LESSON_END_POINT + lesson_id),
    enabled: isAuthenticated && Boolean(lesson_id) && lesson_id !== "", staleTime: 1, // 1 second
  });

  // Private function to update the query cache with the new data.
  // Used in our mutations below as a callback.
  const setLessonData = (lesson: LessonDetail) => {
    queryClient.setQueryData(["my-diary", lesson._id], lesson);
  };

  // Setup up our React Query Mutation for updating the lesson

  const updateLessonMutation = useMutation({
    mutationFn: async (newLesson: LessonDetail) => {
      // Update lesson via API
      const payload = newLesson;
      const l_id = lesson_id ? lesson_id : newLesson._id;
      if (!l_id) {
        throw new Error("ERROR: No lesson id to use in save request.");
      }
      await callApi("PUT", MY_LESSON_END_POINT + l_id + (app.notifyCoverTeachers ? "?notify=y": ""), { data: payload });
      return newLesson;
    },
    onSuccess: setLessonData,
  });

  const updateLesson = async (newLesson: LessonDetail) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { ...newLesson };

      updateLessonMutation.mutate(l);

      return l;
    } catch (error: any) {
      console.error(`ERROR: Could not save lesson.  ${error?.message}`);
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  // Setup up our React Query Mutation for creating the lesson

  const createLessonMutation = useMutation({
    mutationFn: async (newLesson: LessonDetail) => {
      // Create lesson via API
      const payload = newLesson;
      try {
        const lesson = await callApi("POST", MY_LESSON_END_POINT + (app.notifyCoverTeachers ? "?notify=y": ""), {
          data: payload,
        });
        return lesson;
      } catch (error: any) {
        console.error(`ERROR: Could not create lesson.  ${error?.message}`);
        throw error;
      }
    },
    onSuccess: setLessonData,
    onError: (error: any) => {
      console.error(`ERROR: onError.  ${error?.message}`);
      const msg = "ERROR: " + error?.message;
      setIsUpdateError(true);
      setUpdateError(msg);
    },
  });

  const createLesson = async (newLesson: LessonDetail) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { ...newLesson };

      result = await createLessonMutation.mutate(l);

      return l;
    } catch (error: any) {
      console.error(`ERROR: Could not save lesson.  ${error?.message}`);
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };


  const createLessonEventMutation = useMutation({
    mutationFn: async (req: LessonEventRequest) => {
      // Create lesson event via API
      const payload = { ...req, "_id": null };
      try {
        const lesson = await callApi("POST", LESSON_COVER_EVENT_END_POINT + (app.notifyCoverTeachers ? "?notify=y": ""), {
          data: payload,
        });
        return lesson;
      } catch (error: any) {
        console.error(`ERROR: Could not create lesson event.  ${error?.message}`);
        throw error;
      }
    },
    onError: (error: any) => {
      console.error(`ERROR: onError.  ${error?.message}`);
      const msg = "ERROR: " + error?.message;
      setIsUpdateError(true);
      setUpdateError(msg);
    },
  });

  /**
   * Create a lesson linked to an event for a set of periods on a date.
   * @param req  LessonEventRequest
   * @returns 
   */
  const createLessonEvent = async (req: LessonEventRequest) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { ...req };

      result = await createLessonEventMutation.mutate(l);

      return l;
    } catch (error: any) {
      console.error(`ERROR: Could not create lesson event.  ${error?.message}`);
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  // Setup up our React Query Mutation for updating the lesson

  const deleteLessonMutation = useMutation({
    mutationFn: async (lesson: LessonDetail) => {
      // Delete lesson via API
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let payload = lesson;
      const l_id = lesson_id ? lesson_id : lesson._id;
      if (!l_id) {
        throw new Error("ERROR: No lesson id to use in delete request.");
      }
      await callApi("DELETE", MY_LESSON_DELETE_END_POINT + l_id, { });
      // Set DeletedAt to now
      lesson.DeletedAt = new Date().toISOString();
      return lesson;
    },
    onSuccess: setLessonData,
  });

  const deleteLesson = async (lesson: LessonDetail) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { ...lesson };

      deleteLessonMutation.mutate(l);

      return l;
    } catch (error: any) {
      console.error(`ERROR: Could not delete lesson.  ${error?.message}`);
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  const addCoverTeacher = async (teacherId: string, notify: boolean = true) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { _id: teacherId, InLieu: 0 };

      // Update the lesson via API
      const payload = l;
      await callApi(
        "PUT",
        LESSON_COVER_TEACHERS_END_POINT +
          lesson_id +
          (notify && app.notifyCoverTeachers ? "?notify=y" : ""),
        { data: payload }
      );

      // Update the query cache - TODO
      // setLessonData(l);

      return l;
    } catch (error: any) {
      console.error(
        `ERROR: Could not save cover for lesson.  ${error?.message}`
      );
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  const removeCoverTeacher = async (
    teacherId: string,
    notify: boolean = true
  ) => {
    let result = null;
    try {
      setLessonSaving(true);
      const l = { _id: teacherId };

      // Update the lesson via API
      const payload = l;
      await callApi(
        "DELETE",
        LESSON_COVER_TEACHERS_END_POINT +
          lesson_id +
          (notify && app.notifyCoverTeachers ? "?notify=y" : ""),
        { data: payload }
      );

      // Update the query cache - TODO
      // setLessonData(l);

      return l;
    } catch (error: any) {
      console.error(
        `ERROR: Could not save cover for lesson.  ${error?.message}`
      );
    } finally {
      setLessonSaving(false);
      // eslint-disable-next-line no-unsafe-finally
      return result;
    }
  };

  const clearErrors = () => {
    setIsUpdateError(false);
    setUpdateError(null);
  };

  return {
    isLoading,
    isError,
    data,
    error,
    lessonDetail: data,
    lesssonDetailLoaded: !isLoading,
    createLesson,
    createLessonEvent,
    lessonSaving,
    addCoverTeacher,
    removeCoverTeacher,
    isUpdateError,
    updateError,
    clearErrors,
    updateLesson,
    deleteLesson,
  };
}
